<template>
  <div class="upload-images">
    <!-- 文件列表显示 -->
    <div class="image-list">
      <div 
        v-for="(file, index) in fileList" 
        :key="file.uid || index"
        class="image-item"
      >
        <div class="image-preview">
          <img :src="file.url" alt="预览图" />
          <div class="image-actions">
            <i class="el-icon-arrow-left" @click="moveLeft(index)" v-if="index > 0"></i>
            <i class="el-icon-view" @click="previewImage(file)"></i>
            <i class="el-icon-delete" @click="removeImage(index)"></i>
            <i class="el-icon-arrow-right" @click="moveRight(index)" v-if="index < fileList.length - 1"></i>
          </div>
        </div>
      </div>
      
      <!-- 上传按钮 -->
      <div 
        v-if="fileList.length < limit" 
        class="upload-trigger"
        @click="triggerUpload"
        v-loading="uploading"
      >
        <i class="el-icon-plus"></i>
        <div class="upload-text">上传图片</div>
      </div>
    </div>
    
    <!-- 隐藏的文件输入框 -->
    <input 
      type="file" 
      ref="fileInput" 
      @change="handleFileChange" 
      accept="image/jpeg,image/jpg,image/png,image/gif" 
      multiple
      style="display: none;"
    />
    
    <!-- 预览对话框 -->
    <el-dialog :visible.sync="previewVisible" append-to-body>
      <img width="100%" :src="previewImageUrl" alt="预览">
    </el-dialog>
  </div>
</template>

<script>
export default {
  name: 'UploadImages',
  props: {
    value: {
      type: String,
      default: ''
    },
    disabled: {
      type: Boolean,
      default: false
    },
    limit: {
      type: Number,
      default: 9
    },
    maxSize: {
      type: Number,
      default: 20 // MB
    }
  },
  data() {
    return {
      fileList: [],
      uploading: false,
      previewVisible: false,
      previewImageUrl: ''
    };
  },
  watch: {
    value: {
      handler(val) {
        this.initFileList(val);
      },
      immediate: true
    }
  },
  methods: {
    // 初始化文件列表
    initFileList(value) {
      console.log('UploadImages initFileList:', value);
      if (!value) {
        this.fileList = [];
        return;
      }
      
      // 将逗号分隔的字符串转换为文件列表
      const urls = value.split(',').map(url => url.trim()).filter(url => url);
      this.fileList = urls.map((url, index) => ({
        name: `image-${index + 1}`,
        url: url,
        uid: Date.now() + index,
        status: 'success'
      }));
      console.log('UploadImages fileList:', this.fileList);
    },

    // 触发文件选择
    triggerUpload() {
      if (this.disabled || this.uploading) return;
      this.$refs.fileInput.click();
    },

    // 处理文件选择
    async handleFileChange(event) {
      const files = Array.from(event.target.files);
      if (!files.length) return;

      console.log('选择的文件:', files);
      
      // 检查数量限制
      if (this.fileList.length + files.length > this.limit) {
        this.$message.error(`最多只能上传${this.limit}张图片`);
        return;
      }

      // 逐个上传文件
      for (const file of files) {
        await this.uploadSingleFile(file);
      }

      // 清空input以便重新选择
      this.$refs.fileInput.value = '';
    },

    // 上传单个文件
    async uploadSingleFile(file) {
      // 验证文件
      if (!this.validateFile(file)) {
        return;
      }

      this.uploading = true;
      
      try {
        console.log('开始上传文件:', file.name);
        
        // 生成文件名
        const fileName = this.generateFileName(file.name);
        console.log('生成的文件名:', fileName);
        
        // 上传到S3
        const imageUrl = await this.uploadToS3(file, fileName);
        console.log('上传成功，图片URL:', imageUrl);
        
        // 添加到文件列表
        const newFile = {
          name: file.name,
          url: imageUrl,
          uid: Date.now() + Math.random(),
          status: 'success'
        };
        
        this.fileList.push(newFile);
        this.emitValue();
        this.$message.success(`图片 ${file.name} 上传成功`);
        
      } catch (error) {
        console.error('上传失败:', error);
        this.$message.error(`图片 ${file.name} 上传失败: ${error.message}`);
      } finally {
        this.uploading = false;
      }
    },

    // 文件验证
    validateFile(file) {
      // 文件类型验证
      const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif'];
      if (!validTypes.includes(file.type)) {
        this.$message.error(`${file.name}: 只支持上传 JPG、JPEG、PNG、GIF 格式的图片`);
        return false;
      }

      // 文件大小验证
      const isLtMaxSize = file.size / 1024 / 1024 < this.maxSize;
      if (!isLtMaxSize) {
        this.$message.error(`${file.name}: 图片大小不能超过 ${this.maxSize}MB`);
        return false;
      }

      return true;
    },

    // 获取S3配置
    async getS3Config() {
      try {
        console.log('获取S3配置...');
        const { data: resData } = await this.$get("s3/config/info", {});
        console.log('S3配置响应:', resData);
        
        if (resData.result !== 200) {
          throw new Error('获取S3配置失败: ' + (resData.message || '未知错误'));
        }
        return resData.data;
      } catch (error) {
        console.error('获取S3配置失败:', error);
        throw new Error('获取上传配置失败: ' + error.message);
      }
    },

    // 上传到S3
    async uploadToS3(file, fileName) {
      // 检查AWS SDK
      if (typeof AWS === 'undefined') {
        throw new Error('AWS SDK未加载，请检查网络连接');
      }

      // 获取S3配置
      const s3Config = await this.getS3Config();
      console.log('S3配置:', s3Config);
      
      // S3配置
      const accountid = "05e3a3acf3a5440a0f6aa4e94818fac2";
      const accessKeyId = "adfaeaebf3a0adc777a2365aa4f7500a";
      const accessKey = "93f858b5e3e7d01f34dc25fcd0e228c61124872996059c2ec464a0431e0355b4";
      
      const s3 = new AWS.S3({
        params: { Bucket: "static-taotan" },
        endpoint: `https://${accountid}.r2.cloudflarestorage.com`,
        accessKeyId: accessKeyId,
        secretAccessKey: accessKey,
        signatureVersion: "v4",
        region: 'auto'
      });
      
      const albumPhotosKey = "micros/";
      const photoKey = albumPhotosKey + fileName;
      
      console.log('开始S3上传:', {
        bucket: 'static-taotan',
        key: photoKey,
        fileName: fileName
      });

      return new Promise((resolve, reject) => {
        s3.upload(
          {
            Key: photoKey,
            Body: file,
            ContentType: file.type
          },
          (error, data) => {
            if (error) {
              console.error('S3上传错误:', error);
              reject(new Error('S3上传失败: ' + error.message));
            } else {
              console.log('S3上传成功:', data);
              const domain = s3Config.domain;
              const imageUrl = `${domain}/${data.key}`;
              console.log('最终图片URL:', imageUrl);
              resolve(imageUrl);
            }
          }
        );
      });
    },

    // 生成文件名
    generateFileName(originalName) {
      const data = new Date();
      const year = data.getFullYear().toString();
      const month = (data.getMonth() + 1).toString().padStart(2, '0');
      const day = data.getDate().toString().padStart(2, '0');
      const hours = data.getHours().toString().padStart(2, '0');
      const minutes = data.getMinutes().toString().padStart(2, '0');
      const seconds = data.getSeconds().toString().padStart(2, '0');
      const ms = data.getMilliseconds().toString().padStart(3, '0');
      
      const timestamp = year + month + day + hours + minutes + seconds + ms;
      
      // 获取文件扩展名
      const index = originalName.lastIndexOf(".");
      const extension = index > -1 ? originalName.substring(index + 1) : 'jpg';
      
      return `${timestamp}.${extension}`;
    },

    // 预览图片
    previewImage(file) {
      this.previewImageUrl = file.url;
      this.previewVisible = true;
    },

    // 删除图片
    removeImage(index) {
      this.fileList.splice(index, 1);
      this.emitValue();
    },

    // 发出值变化事件
    emitValue() {
      const urls = this.fileList
        .filter(file => file.url && file.status === 'success')
        .map(file => file.url);
      
      const value = urls.join(',');
      console.log('UploadImages emitValue:', value);
      this.$emit('input', value);
    },

    // 向左移动图片
    moveLeft(index) {
      if (index <= 0) return;

      const temp = this.fileList[index];
      this.$set(this.fileList, index, this.fileList[index - 1]);
      this.$set(this.fileList, index - 1, temp);

      this.emitValue();
    },

    // 向右移动图片
    moveRight(index) {
      if (index >= this.fileList.length - 1) return;

      const temp = this.fileList[index];
      this.$set(this.fileList, index, this.fileList[index + 1]);
      this.$set(this.fileList, index + 1, temp);

      this.emitValue();
    }
  }
};
</script>

<style lang="scss" scoped>
.upload-images {
  .image-list {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
  }

  .image-item {
    position: relative;
    width: 100px;
    height: 100px;
    border: 1px solid #d9d9d9;
    border-radius: 6px;
    overflow: hidden;
  }

  .image-preview {
    width: 100%;
    height: 100%;
    position: relative;

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
  }

  .image-actions {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 10px;
    opacity: 0;
    transition: opacity 0.3s;

    i {
      color: #ffffff;
      font-size: 18px;
      cursor: pointer;
      
      &:hover {
        color: #409EFF;
      }
    }
  }

  .image-item:hover .image-actions {
    opacity: 1;
  }

  .upload-trigger {
    width: 100px;
    height: 100px;
    border: 1px dashed #d9d9d9;
    border-radius: 6px;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    cursor: pointer;
    background-color: #fafafa;
    transition: border-color 0.3s;

    &:hover {
      border-color: #409EFF;
    }

    i {
      font-size: 28px;
      color: #8c939d;
      margin-bottom: 8px;
    }

    .upload-text {
      font-size: 12px;
      color: #8c939d;
    }
  }
}
</style> 